import React, { useState, useEffect } from 'react';
import { GetRequest } from 'components/common';
import LibraryAttributePicker from 'components/quote_builder/LibraryAttributePicker.jsx';
import UserUnitPriceCreateForm from 'components/quote_builder/UserUnitPriceCreateForm.jsx';
import QuoteBuilderForm from 'components/quote_builder/QuoteBuilderForm.jsx';

// The /mcc "add a machine" flow, shown INLINE where a section's manufacturing-line list is (under the filter
// island), mirroring EmptyStateMaterialAdder's material form. The library picker runs the two steps in place --
// pick a machine, then its price form REPLACES the picker (a machine with no price cannot quote a line).
//
// "Enter manually" (a machine not in the curated library) runs the SAME two steps: the blank machine form, then
// that machine's price form -- so a manually-entered machine is also priced before it counts.
//
// RE-SOLVE ON PRICE SAVE, not on the copy/create: onCopied only NOTES a machine now exists (so the embedder can
// re-solve on unmount if the flow ends unpriced); a re-solve mid-flow would remount the section and tear this
// down. The one re-solve fires when a price is saved (onPriced), exactly as adding a material does. hideDone
// drops the picker's "Done" -- here the exit is pricing a machine, not leaving the picker.
//
// entryOnly stops after the machine is created -- no price step. Adding the machine IS the whole flow (the price
// is a separate step elsewhere, e.g. the setup wizard's own machine-price stage): the picker runs entryOnly and
// the manual form finishes on save, both via onCopied. onCopied receives the new machine's attribute id, so that
// separate price step can pre-select the machine that was just added. onPriced is unused in that mode.
//
// tabConfig is the machine's settings tab (its UserUnitPriceAttribute tab) the library picker runs on.
// libraryAttributeIds (optional) is handed to the library picker -- see LibraryAttributePicker.
const AddMachineFlow = ({ machineKey, tabConfig, allowedTypes, libraryAttributeIds, onCopied, onPriced, onClose, entryOnly }) => {
  const [manual, setManual] = useState(false);            // the blank machine form is showing
  const [manualConfig, setManualConfig] = useState(null); // its (machine attribute) create config, fetched once
  const [manualPriceFor, setManualPriceFor] = useState(null); // { attributeId } once the manual machine is saved

  useEffect(() => {
    if (manual && !manualConfig) {
      GetRequest({
        url: `/quote-builder/user_unit_price_attributes/config/${machineKey}/${machineKey}`,
        callback: (data) => { setManualConfig(data); },
      });
    }
  }, [manual, manualConfig, machineKey]);

  const backToPicker = () => { setManual(false); setManualPriceFor(null); };

  // Manual step 2: the just-created machine's price form (same as the library-copy price step).
  if (manualPriceFor) {
    return (
      <UserUnitPriceCreateForm
        priceKey={machineKey}
        contextKey={machineKey}
        attributeId={manualPriceFor.attributeId}
        afterSave={() => { backToPicker(); onPriced(); }}
        afterCancel={backToPicker}
      />
    );
  }

  // Manual step 1: the blank machine form. Saving it (the attribute create returns new_attribute_id) leads
  // straight into that machine's price form -- or, in entryOnly mode, finishes the flow (onCopied) with no price.
  if (manual) {
    if (!manualConfig) { return <div className="library-picker__status">{t('quote_builder.please_wait')}</div>; }
    return (
      <QuoteBuilderForm
        config={manualConfig.create_sub_form}
        afterSave={(newAttributeId) => {
          if (onCopied) { onCopied(newAttributeId); }
          if (!entryOnly) { setManualPriceFor({ attributeId: newAttributeId }); }
        }}
        afterCancel={backToPicker}
      />
    );
  }

  return (
    <LibraryAttributePicker
      userUnitPriceKey={machineKey}
      tabConfig={tabConfig}
      onCopy={(newAttributeId) => { if (onCopied) { onCopied(newAttributeId); } }}
      onPriced={onPriced}
      onManualEntry={() => setManual(true)}
      onClose={onClose}
      hideDone
      entryOnly={entryOnly}
      allowedTypes={allowedTypes}
      libraryAttributeIds={libraryAttributeIds}
    />
  );
};

export default AddMachineFlow;
